import React, { useMemo, type HTMLAttributes } from 'react';
import c from 'classnames';

import { TitleWithText } from '@uncinc/uncinc-react-kitchen-sink';
import type { FileField, ImmutableEntity } from '@uncinc/react-drupal-core/types';

import { AudioPlayers, Downloads, VariableRatiosPicture } from '../../../components';
import isAudioParagraph from '../../../helpers/isAudioParagraph';
import type { ParagraphDownloads as Type } from '../../../types/uncinc_profile/paragraphs';

import './style.scss';

interface ParagraphDownloadsProps extends React.HTMLProps<HTMLAttributes<HTMLDivElement>> {
  entity: ImmutableEntity<Type>;
}

const ParagraphDownloads: React.FC<ParagraphDownloadsProps> = ({
  id,
  entity,
}) => {
  const paragraphDownloads = entity.toJS();

  const title = paragraphDownloads.field_title[0]?.value;
  const body = paragraphDownloads.field_body[0]?.processed;
  const [image] = paragraphDownloads.field_media_item;

  const downloads = useMemo(() => paragraphDownloads.field_downloads.map((fieldItem) => {
    let file: FileField;
    if ('field_media_audio_file' in fieldItem.entity) {
      [file] = fieldItem.entity.field_media_audio_file;
    } else if ('field_media_document' in fieldItem.entity) {
      [file] = fieldItem.entity.field_media_document;
    } else {
      [file] = fieldItem.entity.field_media_file;
    }

    return {
      source: file.source,
      title: fieldItem.title,
      caption: fieldItem.entity.field_caption?.[0]?.value,
      filename: file.filename,
      filesize: fieldItem.file_size,
      mime: fieldItem.mime_type,
    };
  }), [paragraphDownloads.field_downloads]);

  const audioParagraph = useMemo(() => isAudioParagraph(paragraphDownloads), [paragraphDownloads]);

  return (
    <div
      id={id}
      className={c('Paragraph ParagraphDownloads col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2', {
        'ParagraphDownloads--audio': audioParagraph,
      })}
    >
      <div className="ParagraphDownloads__header">
        <TitleWithText
          title={title}
          text={body}
        />
      </div>
      <div className="ParagraphDownloads__content">
        {image && (
          <VariableRatiosPicture
            image={image}
            aspectRatios={['16_9', '16_9', '16_9', '1_1', '1_1']}
          />
        )}
        {audioParagraph ? (
          <AudioPlayers
            tracks={downloads}
          />
        ) : (
          <Downloads
            downloads={downloads}
            materialIconName="file_download"
          />
        )}
      </div>
    </div>
  );
};

export default ParagraphDownloads;
